import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  AlertTriangle, Banknote, Briefcase, ChevronRight, FileWarning, Landmark,
  Percent, PiggyBank, ReceiptText, TrendingUp, Wallet,
} from 'lucide-react';
import {
  Area, AreaChart, Bar, BarChart, CartesianGrid, Cell, Legend, Line, LineChart,
  Pie, PieChart, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts';
import { KPICard, Skeleton } from '@/components/ui';
import { getDashboardPatron } from '@/services/dashboard';
import { formatDZD, formatDZDCompact } from '@/utils/algeria';

const DONUT = ['#1a1a2e', '#FFCD11', '#0073EA', '#00C875', '#F97316', '#64748b', '#E2445C'];

function MoneyTooltip({ active, payload, label }: { active?: boolean; payload?: Array<{ value: number }>; label?: string }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-md border border-border bg-surface-secondary px-3 py-2 text-xs shadow-md">
      {label && <p className="mb-1 font-semibold text-content-primary">{label}</p>}
      {payload.map((p, i) => <p key={i} className="tabular-nums text-content-secondary">{formatDZD(p.value)}</p>)}
    </div>
  );
}

function Carte({ title, icon, children, className = '' }: { title: string; icon?: React.ReactNode; children: React.ReactNode; className?: string }) {
  return (
    <div className={`rounded-lg border border-border bg-surface-secondary p-5 shadow-sm ${className}`}>
      <div className="mb-4 flex items-center gap-2">{icon}<h2 className="text-base font-bold text-content-primary">{title}</h2></div>
      {children}
    </div>
  );
}

export function DashboardPatronPage() {
  const navigate = useNavigate();
  const { data, isLoading } = useQuery({ queryKey: ['dashboard-patron'], queryFn: getDashboardPatron });

  if (isLoading || !data) {
    return <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">{Array.from({ length: 8 }).map((_, i) => <Skeleton key={i} className="h-28" />)}</div>;
  }

  const { vitales, sante, rentabilite, top5_ca, pipeline, graphiques, alertes, coherence } = data;
  const nbAlertes = alertes.depassement_budget.length + alertes.cautions.length + alertes.creances_souffrance.length + alertes.retard_planning.length + (alertes.g50.jours <= 15 ? 1 : 0);

  return (
    <div className="space-y-6">
      {/* Constantes vitales */}
      <section>
        <h2 className="label-caps mb-3">Constantes vitales</h2>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
          <KPICard title="Trésorerie disponible" value={formatDZDCompact(vitales.tresorerie)} icon={Banknote} changeLabel="tous comptes" accent="#00C875" sparkline={graphiques.tresorerie_prev.map((t) => t.solde)} />
          <KPICard title="CA facturé (mois)" value={formatDZDCompact(vitales.ca_mois)} icon={Wallet} changePct={vitales.ca_var_pct} changeLabel="vs mois préc." accent="#1a1a2e" sparkline={graphiques.ca_mensuel.map((c) => c.ca)} />
          <KPICard title="Marge globale (mois)" value={formatDZDCompact(vitales.marge_mois)} icon={Percent} changePct={vitales.marge_pct} changeLabel="de marge" accent="#F97316" />
          <KPICard title="Carnet de commandes" value={formatDZDCompact(vitales.carnet_commandes)} icon={Briefcase} changeLabel="reste à facturer" accent="#64748b" />
        </div>
      </section>

      {/* Santé financière */}
      <section>
        <h2 className="label-caps mb-3">Santé financière</h2>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
          <KPICard title="Créances clients" value={formatDZDCompact(sante.creances)} icon={ReceiptText} accent="#1a1a2e"
            changeLabel={sante.creances_60j > 0 ? `dont ${formatDZDCompact(sante.creances_60j)} > 60 j` : 'à jour'} />
          <KPICard title="Dettes fourn. + ST" value={formatDZDCompact(sante.dettes)} icon={Landmark} accent="#F56565" changeLabel="à payer" />
          <KPICard title="Dépenses non comptab." value={formatDZDCompact(sante.depenses_non_comptabilisees)} icon={FileWarning} accent="#F97316" changeLabel={`${sante.non_comptabilisees_pct.toFixed(0)}% des dépenses`} />
        </div>
        {sante.creances_60j > 0 && (
          <p className="mt-2 text-xs font-medium text-status-stuck">⚠ {formatDZD(sante.creances_60j)} de créances de plus de 60 jours.</p>
        )}
      </section>

      {/* Deux résultats DISTINCTS et étiquetés : gestion (réel) vs comptable/fiscal */}
      <section>
        <h2 className="label-caps mb-3">Résultat du mois — gestion vs fiscal</h2>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <KPICard
            title="Résultat économique réel"
            value={formatDZDCompact(sante.resultat_economique_reel)}
            icon={PiggyBank}
            accent={sante.resultat_economique_reel >= 0 ? '#1a1a2e' : '#E2445C'}
            changeLabel="produits − TOUTES charges réelles (pilotage)"
          />
          <KPICard
            title="Résultat comptable / fiscal"
            value={formatDZDCompact(sante.resultat_comptable_fiscal)}
            icon={Landmark}
            accent={sante.resultat_comptable_fiscal >= 0 ? '#64748b' : '#E2445C'}
            changeLabel="produits déclarés − charges déductibles (bilan, G50)"
          />
        </div>
        <p className="mt-2 rounded-md border border-border bg-surface-secondary px-3 py-2 text-xs text-content-secondary">
          Écart = <strong className="tabular-nums text-content-primary">{formatDZD(sante.ecart_charges_non_deductibles)}</strong> de charges réelles non déductibles (dépenses sans pièce conforme).
          C'est une information de gestion assumée, pas une anomalie : le résultat fiscal est plus élevé car il ignore ces charges.
        </p>
      </section>

      {/* Graphiques */}
      <section className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <Carte title="Chiffre d'affaires — 12 mois" className="lg:col-span-2">
          <div className="h-56">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={graphiques.ca_mensuel} margin={{ top: 4, right: 8, left: 8 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
                <XAxis dataKey="label" tick={{ fontSize: 11, fill: '#718096' }} axisLine={false} tickLine={false} />
                <YAxis tickFormatter={(v) => `${(v / 1_000_000).toFixed(0)}M`} tick={{ fontSize: 11, fill: '#718096' }} axisLine={false} tickLine={false} width={34} />
                <Tooltip content={<MoneyTooltip />} cursor={{ fill: '#EDF2F7' }} />
                <Bar dataKey="ca" fill="#1a1a2e" radius={[4, 4, 0, 0]} maxBarSize={34} />
              </BarChart>
            </ResponsiveContainer>
          </div>
          {/* Réconciliation : la carte « CA mois » = dernier bâton ; bornée par les marchés en cours. */}
          <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 border-t border-border pt-2 text-xs text-content-muted">
            <span>Mois en cours : <strong className="text-content-secondary">{formatDZDCompact(vitales.ca_mois)}</strong> (= dernier bâton)</span>
            <span>Cumul annuel : <strong className="text-content-secondary">{formatDZDCompact(vitales.ca_annee)}</strong></span>
            <span>Marchés en cours : <strong className="text-content-secondary">{formatDZDCompact(vitales.marches_en_cours)}</strong></span>
            <span>{vitales.chantiers_actifs} chantier(s) actif(s)</span>
          </div>
          {!coherence.regle3_marches_coherents && (
            <p className="mt-2 rounded-md bg-status-stuck/10 px-2 py-1 text-xs font-medium text-status-stuck">⚠ {coherence.anomalies_marche.length} marché(s) avec un cumul facturé &gt; montant contractuel — données à corriger (voir Marchés).</p>
          )}
        </Carte>
        <Carte title="CA par type de marché">
          <div className="h-64">
            {graphiques.repartition_ca.length ? (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={graphiques.repartition_ca} dataKey="value" nameKey="name" cx="50%" cy="45%" innerRadius={50} outerRadius={82} paddingAngle={2}>
                    {graphiques.repartition_ca.map((e, i) => <Cell key={e.name} fill={DONUT[i % DONUT.length]} />)}
                  </Pie>
                  <Tooltip content={<MoneyTooltip />} />
                  <Legend iconType="circle" wrapperStyle={{ fontSize: 11 }} />
                </PieChart>
              </ResponsiveContainer>
            ) : <p className="flex h-full items-center justify-center text-sm text-content-muted">Aucun marché renseigné.</p>}
          </div>
        </Carte>

        <Carte title="Évolution de la marge — 12 mois">
          <div className="h-56">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={graphiques.marge_mensuelle} margin={{ top: 4, right: 8, left: 8 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
                <XAxis dataKey="label" tick={{ fontSize: 11, fill: '#718096' }} axisLine={false} tickLine={false} />
                <YAxis tickFormatter={(v) => `${(v / 1_000_000).toFixed(0)}M`} tick={{ fontSize: 11, fill: '#718096' }} axisLine={false} tickLine={false} width={34} />
                <Tooltip content={<MoneyTooltip />} />
                <Line type="monotone" dataKey="marge" stroke="#F97316" strokeWidth={2.5} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </Carte>
        <Carte title="Trésorerie prévisionnelle (90 j)" className="lg:col-span-2">
          <div className="h-56">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={graphiques.tresorerie_prev} margin={{ top: 4, right: 8, left: 8 }}>
                <defs><linearGradient id="tp" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#00C875" stopOpacity={0.3} /><stop offset="100%" stopColor="#00C875" stopOpacity={0} /></linearGradient></defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
                <XAxis dataKey="label" tick={{ fontSize: 10, fill: '#718096' }} axisLine={false} tickLine={false} />
                <YAxis tickFormatter={(v) => `${(v / 1_000_000).toFixed(0)}M`} tick={{ fontSize: 11, fill: '#718096' }} axisLine={false} tickLine={false} width={34} />
                <Tooltip content={<MoneyTooltip />} />
                <Area type="monotone" dataKey="solde" stroke="#00C875" strokeWidth={2} fill="url(#tp)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </Carte>
      </section>

      {/* Rentabilité + Pipeline */}
      <section className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <Carte title="Rentabilité des chantiers actifs" icon={<Percent className="size-4 text-brand-primary" />} className="lg:col-span-2">
          {rentabilite.length ? (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="border-b border-border text-left text-xs text-content-muted">
                  <th className="py-2">Chantier</th><th className="py-2 text-right">CA</th><th className="py-2 text-right">Coûts</th><th className="py-2 text-right">Marge</th><th className="py-2 text-right">%</th>
                </tr></thead>
                <tbody>
                  {rentabilite.slice(0, 8).map((c) => (
                    <tr key={c.id} className="cursor-pointer border-b border-border/60 last:border-0 hover:bg-surface-hover" onClick={() => navigate(`/chantiers/${c.id}`)}>
                      <td className="py-1.5 font-medium text-content-primary">{c.nom}</td>
                      <td className="py-1.5 text-right tabular-nums">{formatDZDCompact(c.ca)}</td>
                      <td className="py-1.5 text-right tabular-nums text-content-secondary">{formatDZDCompact(c.cout)}</td>
                      <td className={`py-1.5 text-right font-semibold tabular-nums ${c.marge < 0 ? 'text-status-stuck' : 'text-status-done'}`}>{formatDZDCompact(c.marge)}</td>
                      <td className={`py-1.5 text-right font-semibold tabular-nums ${c.marge < 0 ? 'text-status-stuck' : 'text-status-done'}`}>{c.marge_pct == null ? '—' : `${c.marge_pct.toFixed(0)}%`}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : <p className="text-sm text-content-muted">Aucun chantier actif.</p>}
        </Carte>

        <div className="space-y-4">
          <Carte title="Pipeline commercial" icon={<TrendingUp className="size-4 text-brand-primary" />}>
            <div className="space-y-3">
              <div className="flex items-end justify-between">
                <div><p className="text-2xl font-bold tabular-nums text-content-primary">{pipeline.ao_nombre}</p><p className="text-xs text-content-muted">AO en cours</p></div>
                <div className="text-right"><p className="text-lg font-semibold tabular-nums text-brand-primary">{formatDZDCompact(pipeline.ao_montant)}</p><p className="text-xs text-content-muted">potentiel</p></div>
              </div>
              <div className="rounded-md bg-surface-primary px-3 py-2">
                <div className="flex items-center justify-between text-sm"><span className="text-content-secondary">Taux de transformation (12 m)</span><span className="font-bold text-status-done">{pipeline.taux_transformation.toFixed(0)}%</span></div>
                <div className="mt-1 text-xs text-content-muted">{pipeline.remportes} remporté(s) · {pipeline.perdus} perdu(s)</div>
              </div>
              <button onClick={() => navigate('/appels-offres')} className="flex w-full items-center justify-center gap-1 rounded-md border border-border py-1.5 text-xs font-medium text-content-secondary hover:bg-surface-hover">Voir les appels d'offres <ChevronRight className="size-3.5" /></button>
            </div>
          </Carte>
          <Carte title="Top 5 chantiers (CA)">
            {top5_ca.length ? (
              <ul className="space-y-2">
                {top5_ca.map((c, i) => (
                  <li key={c.id} className="flex items-center gap-2 text-sm">
                    <span className="flex size-5 items-center justify-center rounded bg-brand-primary/10 text-[11px] font-bold text-brand-primary">{i + 1}</span>
                    <button onClick={() => navigate(`/chantiers/${c.id}`)} className="flex-1 truncate text-left text-content-primary hover:text-brand-primary hover:underline">{c.nom}</button>
                    <span className="tabular-nums font-semibold text-content-secondary">{formatDZDCompact(c.ca)}</span>
                  </li>
                ))}
              </ul>
            ) : <p className="text-sm text-content-muted">—</p>}
          </Carte>
        </div>
      </section>

      {/* Alertes prioritaires */}
      <Carte title={`Alertes prioritaires${nbAlertes ? ` (${nbAlertes})` : ''}`} icon={<AlertTriangle className="size-4 text-status-working" />}>
        <div className="grid grid-cols-1 gap-x-6 gap-y-2 md:grid-cols-2">
          {alertes.depassement_budget.map((a) => (
            <AlerteLigne key={`d${a.id}`} couleur="stuck" tag="Dépassement" onClick={() => navigate(a.lien)} texte={`${a.nom} — coûts à ${a.depassement_pct.toFixed(0)}% au-dessus du budget`} />
          ))}
          {alertes.creances_souffrance.map((a, i) => (
            <AlerteLigne key={`c${i}`} couleur="working" tag="Créance" onClick={() => navigate(a.lien)} texte={`${a.chantier} — ${formatDZDCompact(a.montant)} en souffrance${a.jours ? ` (${a.jours} j)` : ''}`} />
          ))}
          {alertes.cautions.map((a, i) => (
            <AlerteLigne key={`b${i}`} couleur="review" tag="Caution" onClick={() => navigate(a.lien)} texte={`${a.reference} — expire dans ${a.jours} j${a.banque ? ` (${a.banque})` : ''}`} />
          ))}
          <AlerteLigne couleur={alertes.g50.jours <= 7 ? 'stuck' : 'info'} tag="G50" onClick={() => navigate(alertes.g50.lien)} texte={`${alertes.g50.libelle} — échéance dans ${alertes.g50.jours} j`} />
          {alertes.retard_planning.map((a) => (
            <AlerteLigne key={`r${a.id}`} couleur="stuck" tag="Retard" onClick={() => navigate(a.lien)} texte={`${a.nom} — ${a.jours_retard} j de retard sur le planning`} />
          ))}
        </div>
        {nbAlertes === 0 && <p className="text-sm text-content-muted">Aucune alerte critique. 🎉</p>}
      </Carte>
    </div>
  );
}

const TAG_BG: Record<string, string> = { stuck: 'bg-status-stuck/15 text-status-stuck', working: 'bg-status-working/15 text-status-working', review: 'bg-purple-500/15 text-purple-600', info: 'bg-brand-primary/15 text-brand-primary' };
function AlerteLigne({ couleur, tag, texte, onClick }: { couleur: string; tag: string; texte: string; onClick: () => void }) {
  return (
    <button onClick={onClick} className="flex w-full items-start gap-2 rounded-md px-2 py-1.5 text-left hover:bg-surface-hover">
      <span className={`mt-0.5 shrink-0 rounded px-1.5 py-0.5 text-[10px] font-bold uppercase ${TAG_BG[couleur] ?? TAG_BG.info}`}>{tag}</span>
      <span className="flex-1 text-sm text-content-secondary">{texte}</span>
      <ChevronRight className="mt-0.5 size-4 shrink-0 text-content-muted" />
    </button>
  );
}
